import { ROUTES, getMasterclassCasesRoute, getMasterclassCaseRoute } from '@/config/routes';

interface Breadcrumb {
  label: string;
  path: string;
}

interface BreadcrumbConfig {
  homeLabel: string;
  caseLabelPrefix: string;
  /** Display name for the case (instead of showing case ID) */
  caseName?: string;
  /** Display name for the masterclass (instead of showing "Cases") */
  masterclassName?: string;
}

/**
 * Generate breadcrumbs for the current page.
 *
 * Route hierarchy:
 * - /masterclasses -> "Home"
 * - /masterclass/:id/cases -> "Home" > "Masterclass"
 * - /masterclass/:id/:caseId -> "Home" > "Masterclass" > "Case"
 * - /masterclass/:id/:caseId/stats -> "Home" > "Masterclass" > "Case" > "Stats"
 */
export const getBreadcrumbs = (
  pathname: string,
  selectedCase: string | null,
  masterclassId?: string | null,
  config?: BreadcrumbConfig
): Breadcrumb[] => {
  const caseLabelPrefix = config?.caseLabelPrefix || 'Case';
  const homeLabel = config?.homeLabel || 'Masterclasses';
  // Use case name if provided, otherwise use case ID
  const caseDisplayName = config?.caseName || selectedCase;
  // Use masterclass name if provided, otherwise use "Masterclass"
  const masterclassDisplayName = config?.masterclassName || 'Masterclass';

  const breadcrumbs: Breadcrumb[] = [];

  // Always start with home
  breadcrumbs.push({ label: homeLabel, path: ROUTES.MASTERCLASS_HOME });

  // Parse masterclass routes
  const masterclassMatch = pathname.match(/^\/masterclass\/([^/]+)/);
  const currentMasterclassId = masterclassMatch?.[1] || masterclassId;

  if (currentMasterclassId) {
    // On masterclass cases page or deeper
    if (pathname.includes('/cases')) {
      breadcrumbs.push({
        label: masterclassDisplayName,
        path: getMasterclassCasesRoute(currentMasterclassId),
      });
    } else if (pathname.includes('/stats') && selectedCase) {
      // On case stats page: Home > Masterclass > Case > Stats
      breadcrumbs.push({
        label: masterclassDisplayName,
        path: getMasterclassCasesRoute(currentMasterclassId),
      });
      breadcrumbs.push({
        label: caseDisplayName ? `${caseLabelPrefix} ${caseDisplayName}` : caseLabelPrefix,
        path: getMasterclassCaseRoute(currentMasterclassId, selectedCase),
      });
      breadcrumbs.push({
        label: 'Stats',
        path: pathname,
      });
    } else if (pathname.includes('/stats')) {
      // On masterclass stats overview page: Home > Masterclass > Stats
      breadcrumbs.push({
        label: masterclassDisplayName,
        path: getMasterclassCasesRoute(currentMasterclassId),
      });
      breadcrumbs.push({
        label: 'Stats',
        path: pathname,
      });
    } else if (selectedCase) {
      // On case detail page: Home > Masterclass > Case
      breadcrumbs.push({
        label: masterclassDisplayName,
        path: getMasterclassCasesRoute(currentMasterclassId),
      });
      breadcrumbs.push({
        label: caseDisplayName ? `${caseLabelPrefix} ${caseDisplayName}` : caseLabelPrefix,
        path: getMasterclassCaseRoute(currentMasterclassId, selectedCase),
      });
    }
  }

  return breadcrumbs;
};
